// Talmia 2.0 · WhatsApp review queue — CONTACT-level.
// One row per phone number (GET /api/whatsapp/contacts). Matching belongs to the
// number: Link / Dismiss / Unlink act on the whole contact and every message.
// Click a row to expand the full conversation inline. Read-only: no send anywhere.
import { useCallback, useEffect, useState, Fragment } from "react";
import Layout from "../../components/Layout";
import api from "../../lib/api";
import { toast } from "sonner";
import { MessageCircle, Search, X, ArrowDownLeft, ArrowUpRight, Loader2, ChevronRight } from "lucide-react";
import { T2Panel, T2Chip } from "../quotes/_t2";

const MATCH_TABS = [
  { k: "",          l: "All" },
  { k: "unmatched", l: "Unmatched" },
  { k: "matched",   l: "Matched" },
  { k: "dismissed", l: "Dismissed" },
];

const fmtDate = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  return isNaN(d) ? "—" : d.toLocaleString("en-GB", { day: "2-digit", month: "short", year: "2-digit", hour: "2-digit", minute: "2-digit" });
};
const fmtTime = (v) => {
  if (!v) return "";
  const d = new Date(v);
  return isNaN(d) ? "" : d.toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
};

function Conversation({ phoneKey }) {
  const [msgs, setMsgs] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get(`/whatsapp/contacts/${encodeURIComponent(phoneKey)}/messages`)
      .then((r) => { if (alive) setMsgs(r.data?.messages || []); })
      .catch(() => { if (alive) setMsgs([]); });
    return () => { alive = false; };
  }, [phoneKey]);

  if (msgs === null) {
    return <div className="py-4 flex justify-center"><Loader2 className="w-4 h-4 animate-spin text-slate-500" /></div>;
  }
  if (msgs.length === 0) {
    return <div className="py-4 text-center text-xs text-slate-500">No messages.</div>;
  }
  return (
    <div className="space-y-2 py-3 px-2 max-h-[380px] overflow-y-auto" data-testid={`whatsapp-conversation-${phoneKey}`}>
      {msgs.map((m) => {
        const out = m.direction === "outbound";
        return (
          <div key={m.message_id} className={`flex ${out ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[78%] rounded-2xl px-3 py-2 border ${out
              ? "bg-orange-500/10 border-orange-500/30 rounded-br-sm"
              : "bg-teal-500/10 border-teal-500/30 rounded-bl-sm"}`}>
              <div className={`flex items-center gap-1.5 mb-0.5 text-[10px] font-mono uppercase tracking-widest ${out ? "text-orange-300" : "text-teal-300"}`}>
                {out ? <><ArrowUpRight className="w-3 h-3" /> Sent</> : <><ArrowDownLeft className="w-3 h-3" /> Received</>}
                <span className="text-slate-500 normal-case tracking-normal">· {fmtTime(m.sent_at)}</span>
              </div>
              <div className={`text-sm whitespace-pre-wrap break-words ${out ? "text-orange-200" : "text-teal-200"}`}>{m.body}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function WhatsApp() {
  const [items, setItems] = useState([]);
  const [facets, setFacets] = useState({ matched: 0, unmatched: 0, dismissed: 0 });
  const [total, setTotal] = useState(0);
  const [match, setMatch] = useState("unmatched");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null); // phone_key

  // Link picker
  const [linkFor, setLinkFor] = useState(null); // phone_key
  const [linkQ, setLinkQ] = useState("");
  const [linkResults, setLinkResults] = useState([]);
  const [linkBusy, setLinkBusy] = useState(false);

  const load = useCallback(async (opts = {}) => {
    const p = opts.page || 1;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (match) params.set("match", match);
      if (search.trim()) params.set("search", search.trim());
      params.set("page", String(p));
      params.set("limit", "50");
      const { data } = await api.get(`/whatsapp/contacts?${params.toString()}`);
      setItems((prev) => (p > 1 ? [...prev, ...(data.items || [])] : (data.items || [])));
      setFacets(data.facets || {});
      setTotal(data.total || 0);
      setPage(p);
    } catch (e) {
      toast.error("Failed to load WhatsApp contacts");
    } finally {
      setLoading(false);
    }
  }, [match, search]);

  useEffect(() => { load({ page: 1 }); }, [load]);

  useEffect(() => {
    const q = linkQ.trim();
    if (q.length < 2) { setLinkResults([]); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/whatsapp/person-search?q=${encodeURIComponent(q)}`);
        if (!cancelled) setLinkResults(data.items || []);
      } catch { if (!cancelled) setLinkResults([]); }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [linkQ]);

  const doLink = async (phoneKey, personId, label) => {
    setLinkBusy(true);
    try {
      await api.post(`/whatsapp/contacts/${encodeURIComponent(phoneKey)}/link`, { person_id: personId });
      toast.success(`Linked contact to ${label}`);
      setLinkFor(null); setLinkQ(""); setLinkResults([]);
      load({ page: 1 });
    } catch (e) {
      toast.error(String(e?.response?.data?.detail || "Link failed"));
    } finally { setLinkBusy(false); }
  };

  const doDismiss = async (phoneKey) => {
    try {
      await api.post(`/whatsapp/contacts/${encodeURIComponent(phoneKey)}/dismiss`);
      toast.success("Dismissed — removed from the unmatched queue");
      load({ page: 1 });
    } catch (e) {
      toast.error(String(e?.response?.data?.detail || "Dismiss failed"));
    }
  };

  const doUnlink = async (phoneKey) => {
    try {
      await api.post(`/whatsapp/contacts/${encodeURIComponent(phoneKey)}/unlink`);
      toast.success("Returned to the unmatched queue");
      load({ page: 1 });
    } catch (e) {
      toast.error(String(e?.response?.data?.detail || "Unlink failed"));
    }
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto py-6 px-4" data-testid="whatsapp-queue-page">
        <T2Panel
          icon={MessageCircle}
          title="WhatsApp"
          testid="whatsapp-queue-panel"
          right={<span className="text-xs text-slate-400" data-testid="whatsapp-total">{total.toLocaleString()} contacts</span>}
        >
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <div style={{ display: "inline-flex", border: "1px solid var(--t2-border2)", borderRadius: 8, overflow: "hidden" }} data-testid="whatsapp-match-filter">
              {MATCH_TABS.map(({ k, l }) => {
                const allCount = (facets?.matched || 0) + (facets?.unmatched || 0) + (facets?.dismissed || 0);
                const count = k ? facets?.[k] : allCount;
                const active = match === k;
                return (
                  <button
                    key={k || "all"}
                    onClick={() => setMatch(k)}
                    data-testid={`whatsapp-match-${k || "all"}`}
                    style={{
                      padding: "6px 14px", fontSize: 13, fontWeight: 600, border: "none", cursor: "pointer",
                      background: active ? (k === "unmatched" ? "rgba(239,68,68,.15)" : "var(--t2-orange-dim)") : "var(--t2-surface)",
                      color: active ? (k === "unmatched" ? "var(--t2-red)" : "var(--t2-orange)") : "var(--t2-grey2)",
                    }}
                  >
                    {l}{count != null ? ` (${count.toLocaleString()})` : ""}
                  </button>
                );
              })}
            </div>
            <div className="relative flex-1 min-w-[220px]">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <input
                className="t2-input pl-9 w-full"
                placeholder="Search name or phone…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && load({ page: 1 })}
                data-testid="whatsapp-search-input"
              />
            </div>
          </div>

          {/* Table */}
          <div className="t2-tbl-wrap overflow-x-auto">
            <table className="t2-tbl w-full" data-testid="whatsapp-table">
              <thead>
                <tr>
                  <th style={{ textAlign: "left" }}>Contact</th>
                  <th style={{ textAlign: "left" }}>Last message</th>
                  <th style={{ textAlign: "left", width: 70 }}>Msgs</th>
                  <th style={{ textAlign: "left", width: 130 }}>Last</th>
                  <th style={{ textAlign: "left", width: 250 }}>Match</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && !loading && (
                  <tr><td colSpan={5} style={{ textAlign: "center", padding: 32, color: "var(--t2-grey2)" }} data-testid="whatsapp-empty">
                    No WhatsApp contacts match the current filters.
                  </td></tr>
                )}
                {items.map((c) => {
                  const isOpen = expanded === c.phone_key;
                  return (
                    <Fragment key={c.phone_key}>
                      <tr
                        onClick={() => setExpanded(isOpen ? null : c.phone_key)}
                        style={{ cursor: "pointer" }}
                        data-testid={`whatsapp-row-${c.phone_key}`}
                      >
                        <td>
                          <div className="flex items-center gap-1.5">
                            <ChevronRight className={`w-3.5 h-3.5 text-slate-500 shrink-0 transition-transform ${isOpen ? "rotate-90" : ""}`} />
                            <div>
                              <div className="font-medium text-slate-200 text-sm">{c.name || "—"}</div>
                              <div className="text-xs text-slate-500 font-mono">{c.phone || ""}</div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="text-sm text-slate-300 max-w-[360px] truncate" title={c.last_message_preview}>{c.last_message_preview || "—"}</div>
                        </td>
                        <td className="text-sm text-slate-300 tabular-nums" data-testid={`whatsapp-count-${c.phone_key}`}>{c.message_count}</td>
                        <td className="text-xs text-slate-400 whitespace-nowrap">{fmtDate(c.last_message_at)}</td>
                        <td onClick={(e) => e.stopPropagation()} data-testid={`whatsapp-match-cell-${c.phone_key}`}>
                          {c.MatchStatus === "matched" ? (
                            <div className="flex items-center gap-2 flex-wrap">
                              <T2Chip tone="ok">Matched</T2Chip>
                              {c.customer_name && <span className="text-xs text-emerald-300 truncate max-w-[120px]" title={c.customer_name}>{c.customer_name}</span>}
                              <button
                                onClick={() => doUnlink(c.phone_key)}
                                data-testid={`whatsapp-unlink-${c.phone_key}`}
                                className="text-xs px-2 py-1 rounded bg-white/5 text-slate-400 hover:bg-white/10 transition-colors"
                              >Unlink</button>
                            </div>
                          ) : c.MatchStatus === "dismissed" ? (
                            <div className="flex items-center gap-2 flex-wrap">
                              <T2Chip tone="">Dismissed</T2Chip>
                              <button
                                onClick={() => { setLinkFor(c.phone_key); setLinkQ(""); setLinkResults([]); }}
                                data-testid={`whatsapp-link-${c.phone_key}`}
                                className="text-xs px-2 py-1 rounded bg-teal-500/15 text-teal-300 hover:bg-teal-500/25 transition-colors"
                              >Link</button>
                              <button
                                onClick={() => doUnlink(c.phone_key)}
                                data-testid={`whatsapp-unlink-${c.phone_key}`}
                                className="text-xs px-2 py-1 rounded bg-white/5 text-slate-400 hover:bg-white/10 transition-colors"
                              >Restore</button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              <T2Chip tone="red">Unmatched</T2Chip>
                              <button
                                onClick={() => { setLinkFor(c.phone_key); setLinkQ(""); setLinkResults([]); }}
                                data-testid={`whatsapp-link-${c.phone_key}`}
                                className="text-xs px-2 py-1 rounded bg-teal-500/15 text-teal-300 hover:bg-teal-500/25 transition-colors"
                              >Link</button>
                              <button
                                onClick={() => doDismiss(c.phone_key)}
                                data-testid={`whatsapp-dismiss-${c.phone_key}`}
                                className="text-xs px-2 py-1 rounded bg-white/5 text-slate-400 hover:bg-white/10 transition-colors"
                              >Dismiss</button>
                            </div>
                          )}
                        </td>
                      </tr>
                      {isOpen && (
                        <tr data-testid={`whatsapp-conv-row-${c.phone_key}`}>
                          <td colSpan={5} style={{ background: "rgba(255,255,255,.015)" }}>
                            <Conversation phoneKey={c.phone_key} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Load more */}
          <div className="flex items-center justify-center mt-4">
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin text-slate-500" data-testid="whatsapp-loading" />
            ) : items.length < total ? (
              <button
                onClick={() => load({ page: page + 1 })}
                data-testid="whatsapp-load-more"
                className="text-sm px-4 py-2 rounded-lg bg-white/5 text-slate-300 hover:bg-white/10 transition-colors"
              >Load more ({items.length} of {total.toLocaleString()})</button>
            ) : null}
          </div>
        </T2Panel>
      </div>

      {/* Link-to-customer modal */}
      {linkFor && (
        <div onClick={() => setLinkFor(null)} data-testid="whatsapp-link-modal"
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.6)", zIndex: 60, display: "flex", alignItems: "flex-start", justifyContent: "center", paddingTop: "12vh" }}>
          <div onClick={(e) => e.stopPropagation()}
            style={{ width: 480, maxWidth: "92vw", background: "var(--t2-panel, #0E1524)", border: "1px solid var(--t2-border2)", borderRadius: 12, padding: 20 }}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-white font-semibold">Link contact to customer</h3>
              <button onClick={() => setLinkFor(null)} data-testid="whatsapp-link-close" className="text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-xs text-slate-500 mb-3">All messages from this number will follow the customer.</p>
            <input autoFocus className="t2-input w-full mb-3" value={linkQ} onChange={(e) => setLinkQ(e.target.value)}
              placeholder="Search customer by name, email or phone…" data-testid="whatsapp-link-search" />
            <div className="max-h-[320px] overflow-y-auto flex flex-col gap-1">
              {linkResults.length === 0 && linkQ.trim().length >= 2 && (
                <div className="text-sm text-slate-500 px-2 py-3">No customers found.</div>
              )}
              {linkResults.map((r) => (
                <button key={r.id} disabled={linkBusy} onClick={() => doLink(linkFor, r.id, r.label)}
                  data-testid={`whatsapp-link-result-${r.id}`}
                  className="text-left px-3 py-2 rounded-lg hover:bg-white/5 transition-colors disabled:opacity-50">
                  <div className="text-sm text-slate-200 font-medium">{r.label}</div>
                  <div className="text-xs text-slate-500">{r.sublabel}{r.meta ? ` · ${r.meta}` : ""}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
